import { lazy } from 'react';
import { IRoute } from 'AurionCR/components/router';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import { PERMISSIONS } from './constants';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import EventIcon from '@mui/icons-material/Event';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AssignmentIcon from '@mui/icons-material/Assignment';

interface IRouteApp extends IRoute {
  permission?: PERMISSIONS;
  routes?: undefined | IRouteApp[];
}

const routes: IRouteApp[] = [
  {
    path: '/',
    title: 'dashboard',
    icon: DashboardIcon,
    component: lazy(() => import(/* webpackChunkName: 'Dashboard' */ 'pages/Dashboard')),
  },
  {
    path: '/members',
    title: 'members',
    // permission: PERMISSIONS.EDIT_CRM_USERS,
    icon: PeopleIcon,
    routes: [
      {
        path: '/members-list',
        title: 'members-list',
        component: lazy(
          () => import(/* webpackChunkName: 'ExpertsList' */ 'pages/Members/members-list'),
        ),
      },
      // {
      //   path: '/visitors-list',
      //   title: 'visitors-list',
      //   component: lazy(
      //     () => import(/* webpackChunkName: 'VisitorsList' */ 'pages/Members/visitors-list'),
      //   ),
      // },
      {
        path: '/messages',
        title: 'chat-messages',
        component: lazy(
          () => import(/* webpackChunkName: 'ChatMessages' */ 'pages/Members/chat-messages'),
        ),
      },
    ],
  },
  {
    path: '/companies',
    title: 'companies',
    icon: LocalPharmacyIcon,
    routes: [
      {
        path: '/index',
        title: 'companies-list',
        component: lazy(() => import(/* webpackChunkName: 'Pharma' */ 'pages/PharmaList')),
      },
    ],
  },
  {
    path: '/posts',
    title: 'articles',
    icon: AssignmentIcon,
    routes: [
      {
        path: '/index',
        title: 'members-posts',
        component: lazy(() => import(/* webpackChunkName: 'Articles' */ 'pages/ArticlesMember')),
      },
      {
        path: '/posts',
        title: 'companies-posts',
        component: lazy(() => import(/* webpackChunkName: 'Articles' */ 'pages/ArticlesCompany')),
      },
    ],
  },
  {
    path: '/events',
    title: 'events',
    icon: EventIcon,
    private: true,
    routes: [
      {
        path: '/events-list',
        private: true,
        title: 'events-list',
        component: lazy(
          () => import(/* webpackChunkName: 'VirtualEvents' */ 'pages/VirtualEvents'),
        ),
      },
    ],
  },
  {
    path: '/surveys',
    title: 'surveys',
    icon: ListAltIcon,
    routes: [
      {
        path: '/surveys-list',
        title: 'surveys-list',
        component: lazy(() => import(/* webpackChunkName: 'Surveys' */ 'pages/SurveysList')),
      },
    ],
  },
  {
    path: '/campaigns',
    title: 'campaigns',
    icon: TrackChangesIcon,
    routes: [
      {
        path: '/campaign-list',
        title: 'campaign-list',
        component: lazy(() => import(/* webpackChunkName: 'Campaigns' */ 'pages/Campaigns')),
      },
      {
        path: '/email-templates',
        title: 'email-templates',
        component: lazy(
          () => import(/* webpackChunkName: 'EmailTemplates' */ 'pages/EmailTemplates'),
        ),
      },
      {
        path: '/sms-messages',
        title: 'sms-messages',
        component: lazy(() => import(/* webpackChunkName: 'SMSContents' */ 'pages/SMSContents')),
      },
    ],
  },
  {
    path: '/app-settings',
    title: 'app-settings',
    icon: SettingsApplicationsIcon,
    // permission: PERMISSIONS.EDIT_WEBSITES,
    routes: [
      {
        path: '/tags',
        title: 'tags',
        component: lazy(() => import(/* webpackChunkName: 'AppPage' */ 'pages/Tags')),
      },
      {
        path: '/cities',
        title: 'cities',
        component: lazy(() => import(/* webpackChunkName: 'Cities' */ 'pages/Cities')),
      },
      {
        path: '/industries',
        title: 'industries',
        component: lazy(() => import(/* webpackChunkName: 'Industries' */ 'pages/Industries')),
      },
      {
        path: '/field-of-occupations',
        title: 'field-of-occupations',
        component: lazy(
          () => import(/* webpackChunkName: 'FieldOfOccupations' */ 'pages/FieldOfOccupations'),
        ),
      },
      {
        path: '/expert-types',
        title: 'expert-types',
        permission: PERMISSIONS.EDIT_PERMISSIONS,
        component: lazy(() => import(/* webpackChunkName: 'ExpertTypes' */ 'pages/ExpertTypes')),
      },
      {
        path: '/article-categories',
        title: 'article-categories',
        component: lazy(
          () => import(/* webpackChunkName: 'ArticleCategories' */ 'pages/ArticleCategories'),
        ),
      },
      {
        path: '/speak-languages',
        title: 'speak-languages',
        component: lazy(
          () => import(/* webpackChunkName: 'SpeakLanguages' */ 'pages/SpeakLanguages'),
        ),
      },
    ],
  },
  {
    path: '/contents',
    title: 'contents',
    icon: AnnouncementIcon,
    routes: [
      {
        path: '/privacy-policy',
        title: 'privacy-policy',
        component: lazy(
          () => import(/* webpackChunkName: 'PrivacyPolicy' */ 'pages/CommonPages/privacy'),
        ),
      },
      {
        path: '/terms-and-conditions',
        title: 'terms-and-conditions',
        component: lazy(
          () => import(/* webpackChunkName: 'TermsAndConditions' */ 'pages/CommonPages/terms'),
        ),
      },
      {
        path: '/labels-crm',
        title: 'labels-crm',
        component: lazy(() => import(/* webpackChunkName: 'Labels' */ 'pages/Labels/crm')),
      },
      {
        path: '/labels-expert',
        title: 'labels-expert',
        component: lazy(() => import(/* webpackChunkName: 'Labels' */ 'pages/Labels/expert')),
      },
      {
        path: '/labels-web',
        title: 'labels-website',
        component: lazy(() => import(/* webpackChunkName: 'Labels' */ 'pages/Labels/websites')),
      },
    ],
  },
  {
    path: '/settings',
    title: 'settings',
    icon: SettingsIcon,
    permission: PERMISSIONS.EDIT_SETTINGS,
    routes: [
      {
        path: '/users',
        title: 'users-crm',
        component: lazy(() => import(/* webpackChunkName: 'Users' */ 'pages/Users')),
      },
      {
        path: '/permissions',
        title: 'permissions',
        permission: PERMISSIONS.EDIT_PERMISSIONS,
        component: lazy(() => import(/* webpackChunkName: 'Permissions' */ 'pages/Permissions')),
      },
    ],
  },
  {
    path: '/profile',
    title: 'profile',
    inMenu: false,
    component: lazy(() => import(/* webpackChunkName: 'Profile' */ 'pages/Profile')),
  },
  {
    path: '/login',
    title: 'login',
    inMenu: false,
    private: false,
    inLayout: false,
    component: lazy(() => import(/* webpackChunkName: 'Login' */ 'pages/Login')),
  },
];

export default routes;
